import type {BetterAuthUser} from '@api/auth';
import {resolveMenuItemEnabled} from '@common/menuFlags';

// TODO: Refactor — evaluate against the broader codebase for a cleaner pattern
// (e.g. server-driven landing destination, hostname/subdomain handling, better
// "wrong portal" UX). Tracked in:
// https://app.asana.com/1/1206214889537900/project/1212886114840713/task/1214550113535366?focus=true
const CORPORATE_DOMAIN = 'cbs-infinity.com';
const CORPORATE_REDIRECT_ERROR = 'Please use the group portal URL to log in.';

export interface PostLoginRedirect {
  errorMessage?: string;
  redirectTo: string | null;
}

/**
 * A group user's portal "home". Single-group users land directly on their
 * group — on the Open Enrollment dashboard by default (the tenant menu flag is
 * on unless an internal admin turns it off), otherwise on the group detail
 * page. Multi-group users pick from the list. Resolved through
 * `resolveMenuItemEnabled` (not the raw feature flag) so the landing route
 * always matches the routes `ClientApp` registers.
 */
export function groupUserHomePath(groupProfiles: BetterAuthUser['groupProfiles']): string {
  if (!groupProfiles || groupProfiles.length !== 1) {
    return '/groups';
  }
  const profile = groupProfiles[0];
  // A group may not be linked to VBA yet — without a vbaGroupId there is no
  // group route to land on, so fall back to the list.
  if (!profile.vbaGroupId) {
    return '/groups';
  }
  return resolveMenuItemEnabled('group.openEnrollment', profile.features)
    ? `/groups/${profile.vbaGroupId}/open-enrollment`
    : `/groups/${profile.vbaGroupId}`;
}

/**
 * A return path taken from the URL (e.g. `?redirect=`), if it is a path in
 * this app. Absolute and protocol-relative URLs are dropped, so a crafted link
 * can't send a user who has just signed in to another site.
 */
export function safeReturnPath(value: string | null | undefined): string | undefined {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return undefined;
  }
  return value;
}

// Decide where the user lands after a successful sign-in based on profile shape and host.
// Returning `errorMessage` signals "show the message and stay on /login" (e.g. wrong portal URL).
export function resolvePostLoginRedirect(
  user: BetterAuthUser,
  returnTo: string | undefined,
  hostname: string = window.location.hostname,
): PostLoginRedirect {
  if (returnTo) {
    return {redirectTo: returnTo};
  }

  const isCorporateDomain = hostname === CORPORATE_DOMAIN;

  if (user.cbsInternalProfile) {
    return {redirectTo: '/'};
  }

  if (user.clientProfile) {
    return {redirectTo: '/groups'};
  }

  if (user.groupProfiles && user.groupProfiles.length > 0) {
    if (isCorporateDomain) {
      return {errorMessage: CORPORATE_REDIRECT_ERROR, redirectTo: null};
    }
    return {redirectTo: groupUserHomePath(user.groupProfiles)};
  }

  if (user.brokerProfile) {
    return {redirectTo: '/broker'};
  }

  if (isCorporateDomain) {
    return {errorMessage: CORPORATE_REDIRECT_ERROR, redirectTo: null};
  }

  return {redirectTo: '/'};
}
